import {
  createBigDecimal,
  greaterThan,
  minus,
  plus,
} from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { CostBasis } from '../../types/costBasis'
import type { Loan, LoanPosition } from '../../types/loan'
import type { Transaction } from '../../types/transaction'
import { createAssetKey } from '../../utils'
import { mergeActionTransfers } from '../../utils/mergeActionTransfers'
import { createBlankTransaction } from './createBlankTransaction'
import { createFeeTransaction } from './createFeeTransaction'
import { createIncomeTransaction } from './createIncomeTransaction'
import { createRepaymentTransaction } from './createRepaymentTransaction'
import { createTransactionsByTradeAction } from './createTransactionsByTradeAction'
import type {
  CreateTransactionsByCategoryActionParameters,
  CreateTransactionsByCategoryActionReturnTypes,
} from './types'
import { createInsufficientTransactionByTransfer } from './util/createInsufficientTransactionByTransfer.ts'

type CreateTransactionsByRepaymentActionParameters =
  CreateTransactionsByCategoryActionParameters & {
    readonly costBasis: CostBasis
    readonly handleLoanAsTrade?: boolean
    readonly use5PercentRule?: boolean
  }

type CreateTransactionsByRepaymentActionReturnTypes =
  CreateTransactionsByCategoryActionReturnTypes

export const createTransactionsByRepaymentAction = ({
  actionId,
  action,
  loans,
  crossActionBundles,
  assetBalances,
  assetBorrows,
  usedBalances,
  nftAcquiredTimestamps,
  costBasis,
  isLocked = false,
  handleLoanAsTrade = false,
  use5PercentRule = false,
}: CreateTransactionsByRepaymentActionParameters): CreateTransactionsByRepaymentActionReturnTypes => {
  // locked action の場合処理しない
  if (isLocked) {
    return {
      transactions: [],
      loans,
      crossActionBundles,
      assetBalances,
      assetBorrows,
      usedBalances,
    }
  }

  if (handleLoanAsTrade) {
    return createTransactionsByTradeAction({
      actionId,
      action,
      loans,
      crossActionBundles,
      assetBalances,
      assetBorrows,
      usedBalances,
      nftAcquiredTimestamps,
      isLocked,
      costBasis,
      use5PercentRule,
    })
  }

  if (action.loanId === undefined) {
    action.loanId = ''
  }

  // merge transfers
  const transfers = mergeActionTransfers(action.transfers)
  const transferCount: { all: number; in: number; out: number } = {
    all: 0,
    in: 0,
    out: 0,
  }
  const inTransfers: Transfer[] = []
  const outTransfers: Transfer[] = []
  for (const transfer of transfers) {
    transferCount.all += 1
    if (transfer.direction === 'in') {
      transferCount.in += 1
      inTransfers.push(transfer)
    }
    if (transfer.direction === 'out') {
      transferCount.out += 1
      outTransfers.push(transfer)
    }
  }

  let newAssetBalances = assetBalances
  let newAssetBorrows = assetBorrows
  let newUsedBalances = usedBalances
  const transactions: Transaction[] = []

  if (transferCount.all === 0) {
    const result = createBlankTransaction({
      actionId,
      timestamp: action.timestamp,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
    return {
      transactions,
      loans,
      crossActionBundles,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    }
  }

  // update loan
  const loan: Loan = loans.get(action.loanId) ?? {
    id: action.loanId,
    borrows: new Map(),
    borrowsWithDebt: new Map(),
    deposits: new Map(),
    depositsWithBond: new Map(),
  }
  const repaymentTransfers: Transfer[] = []
  const interestFeeTransfers: Transfer[] = []
  for (const transfer of outTransfers) {
    const assetKey = createAssetKey(transfer.asset)
    let remainAmount = transfer.amount
    let repaymentAmount = createBigDecimal(0n)
    const targetBorrows = loan.borrows.get(assetKey) ?? []
    const newBorrows: LoanPosition[] = []
    for (const borrow of targetBorrows) {
      if (remainAmount.value === 0n) {
        newBorrows.push(borrow)
      } else if (greaterThan(remainAmount, borrow.amount)) {
        repaymentAmount = plus(repaymentAmount, borrow.amount)
        remainAmount = minus(remainAmount, borrow.amount)
      } else {
        const newBorrowAmount = minus(borrow.amount, remainAmount)
        repaymentAmount = plus(repaymentAmount, remainAmount)
        remainAmount = createBigDecimal(0n)
        if (newBorrowAmount.value > 0n) {
          newBorrows.push({
            ...borrow,
            amount: newBorrowAmount,
          })
        }
      }
    }
    if (newBorrows.length > 0) {
      loan.borrows.set(assetKey, newBorrows)
    } else {
      loan.borrows.delete(assetKey)
    }

    if (repaymentAmount.value > 0n) {
      repaymentTransfers.push({
        ...transfer,
        amount: repaymentAmount,
      })
    }
    if (remainAmount.value > 0n) {
      interestFeeTransfers.push({
        ...transfer,
        amount: remainAmount,
      })
    }
  }
  if (
    loan.deposits.size > 0 ||
    loan.depositsWithBond.size > 0 ||
    loan.borrows.size > 0 ||
    loan.borrowsWithDebt.size > 0
  ) {
    loans.set(loan.id, loan)
  } else {
    loans.delete(loan.id)
  }

  for (const transfer of repaymentTransfers) {
    const insufficientResult = createInsufficientTransactionByTransfer({
      actionId,
      timestamp: action.timestamp,
      transfer,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
      costBasis,
      onlyUseBorrows: true,
    })
    if (insufficientResult !== undefined) {
      transactions.push(insufficientResult.transaction)
      newAssetBalances = insufficientResult.assetBalances
      newAssetBorrows = insufficientResult.assetBorrows
      newUsedBalances = insufficientResult.usedBalances
    }

    const result = createRepaymentTransaction({
      actionId,
      timestamp: action.timestamp,
      transfer,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
  }

  for (const transfer of interestFeeTransfers) {
    const insufficientResult = createInsufficientTransactionByTransfer({
      actionId,
      timestamp: action.timestamp,
      transfer,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
      costBasis,
    })
    if (insufficientResult !== undefined) {
      transactions.push(insufficientResult.transaction)
      newAssetBalances = insufficientResult.assetBalances
      newAssetBorrows = insufficientResult.assetBorrows
      newUsedBalances = insufficientResult.usedBalances
    }

    const result = createFeeTransaction({
      actionId,
      timestamp: action.timestamp,
      subCategory: 'interest-fee',
      transfer,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
      costBasis,
      use5PercentRule,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
  }

  for (const transfer of inTransfers) {
    const result = createIncomeTransaction({
      actionId,
      timestamp: action.timestamp,
      subCategory: 'other-income',
      transfer,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
      costBasis,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
  }

  return {
    transactions,
    loans,
    crossActionBundles,
    assetBalances: newAssetBalances,
    assetBorrows: newAssetBorrows,
    usedBalances: newUsedBalances,
  }
}
